import { Body, Button, Dialog, Spacer } from '@spritz-finance/ui'
import styled from 'styled-components'
import metamaskLogo from '../../assets/icons/metamask.svg'
import { FadePresence } from '../../components/FadePresence'
import { Column } from '../../components/Layout/Column'
import { Row } from '../../components/Layout/Row'
import { FeedbackForm, useFeedback } from '../../context/useFeedback'
import { isMobile } from '../../utils/userAgent'
import { useConnectWallet } from '../connection/useConnectWallet'
import { getWalletType } from '../connection/utils'
import { useDisconnectWallet, useWalletActivated } from '../walletOptions/useWalletConnection'
import { WalletOptions } from '../walletOptions/WalletOptions'
import { useWeb3Connection } from '../Web3Provider'
import { useWalletDialog, WalletView } from './WalletConnectDialogProvider'

const Logo = styled.img`
    width: 22px;
    position: absolute;
    top: -1px;
    right: -27px;
`

const Options = () => {
    const { openFeedbackForm } = useFeedback()
    const connectText = isMobile ? (
        <>
            <Body.Med size={14}>
                You can use WalletConnect to connect to popular wallets from any device, including
            </Body.Med>
            <Body.Med size={14} style={{ position: 'relative', marginLeft: '5px' }}>
                MetaMask
                <Logo src={metamaskLogo} alt="Wallet Connect Logo" />
            </Body.Med>
        </>
    ) : (
        <>
            <Body.Med size={14}>
                You can use WalletConnect to connect to popular wallets from any device, including
                TrustWallet or Ledger.
            </Body.Med>
        </>
    )

    return (
        <>
            <Dialog.Title>Connect your wallet</Dialog.Title>
            <Spacer height={16} />
            <WalletOptions />
            <Spacer height={32} />
            <Column>
                <Body.Bold size={14}>Need a refresher?</Body.Bold>
                <Spacer height={8} />
                <Row
                    style={{
                        flexWrap: 'wrap',
                        maxWidth: 418,
                        lineHeight: '20px',
                        display: 'inline',
                    }}
                >
                    {connectText}
                </Row>
            </Column>
            <Spacer height={32} />
            <Row align="center">
                <Button.CTA responsive onClick={() => openFeedbackForm(FeedbackForm.Wallet)}>
                    Problems connecting?
                </Button.CTA>
            </Row>
            <Spacer height={16} />
        </>
    )
}

const Account = () => {
    const { closeDialog } = useWalletDialog()
    const { connector } = useWeb3Connection()
    const disconnect = useDisconnectWallet()
    const walletType = getWalletType(connector)

    const handleDisconnect = () => {
        disconnect()
        closeDialog()
    }

    return (
        <>
            <Dialog.Title>Account</Dialog.Title>
            <Spacer height={16} />
            <Column>
                <Row align="center" style={{ width: '100%' }}>
                    <Body.SmBold align="center">Connected with {walletType}</Body.SmBold>
                </Row>
                <Spacer height={32} />
                <Row align="center" style={{ width: '100%' }}>
                    <Button.CTA onClick={handleDisconnect}>Disconnect</Button.CTA>
                </Row>
            </Column>
        </>
    )
}

const Connecting = () => {
    const { error, pendingConnector, closeDialog, setWalletView } = useWalletDialog()
    const activate = useConnectWallet(closeDialog)

    return (
        <>
            <Dialog.Title>Account</Dialog.Title>
            <Spacer height={16} />
            <FadePresence renderKey={error}>
                {error ? (
                    <Column
                        align="center"
                        style={{
                            flexWrap: 'wrap',
                            maxWidth: 418,
                        }}
                    >
                        <Body.Bold size={18} align="center">
                            Error connecting
                        </Body.Bold>
                        <Spacer height={16} />
                        <Body.Reg size={14} align="center">
                            The connection attempt failed. Please click try again and follow the
                            steps to connect in your wallet.
                        </Body.Reg>
                        <Spacer height={32} />
                        <Button.CTA
                            onClick={() => {
                                if (pendingConnector) activate(pendingConnector)
                            }}
                        >
                            Try again
                        </Button.CTA>
                        <Spacer height={32} />
                        <Button.Text onClick={() => setWalletView(WalletView.Options)}>
                            Go back to wallet selection
                        </Button.Text>
                    </Column>
                ) : (
                    <Column align="center">
                        <Body.Bold size={18} align="center">
                            Connecting to wallet...
                        </Body.Bold>
                    </Column>
                )}
            </FadePresence>
        </>
    )
}

export const WalletConnectContent = () => {
    const { view } = useWalletDialog()
    useWalletActivated()
    const DialogComponent = {
        [WalletView.Options]: <Options />,
        [WalletView.Account]: <Account />,
        [WalletView.Connecting]: <Connecting />,
    }
    return <FadePresence renderKey={view}>{DialogComponent[view]}</FadePresence>
}
